import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import ProductIntro from "@/components/ProductIntro";
import Differentiators from "@/components/Differentiators";
import CompanySection from "@/components/CompanySection";
import CompanyProfile from "@/components/CompanyProfile";
import ContactSection from "@/components/ContactSection";
import WaitlistModal from "@/components/WaitlistModal";
import Footer from "@/components/Footer";
import LegalPage from "@/components/LegalPage";

function HomePage() {
  return (
    <main data-testid="home-page">
      <CompanySection />
      <CompanyProfile />
    </main>
  );
}

function ContactPage() {
  return (
    <main data-testid="contact-page">
      <ContactSection />
    </main>
  );
}

function TafakerPage({ onJoin }) {
  return (
    <main data-testid="tafaker-page">
      <Hero onJoin={onJoin} />
      <Marquee />
      <ProductIntro onJoin={onJoin} />
      <Differentiators />
    </main>
  );
}

function ScrollManager({ lenisRef }) {
  const location = useLocation();
  useEffect(() => {
    const lenis = lenisRef.current;
    const el = location.hash ? document.querySelector(location.hash) : null;
    if (el) {
      setTimeout(() => (lenis ? lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView()), 60);
    } else {
      lenis ? lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
    }
  }, [location.key, location.hash, lenisRef]);
  return null;
}

function Site() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const openWaitlist = useCallback(() => setWaitlistOpen(true), []);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ anchors: true, duration: 1.1 });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-slate-900" data-testid="app-root">
      <ScrollManager lenisRef={lenisRef} />
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/tafakar" element={<TafakerPage onJoin={openWaitlist} />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="*" element={<HomePage />} />
      </Routes>
      <Footer />
      <WaitlistModal open={waitlistOpen} onOpenChange={setWaitlistOpen} />
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Site />
      </BrowserRouter>
    </LanguageProvider>
  );
}
